// ./src/hooks/useContentBlocks.ts
import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { OP_RESULT_CODES } from "@sdflc/api-helpers";
import {
  ContentBlock,
  ContentBlockService,
  ContentBlockFilter,
  ContentBlockInput,
  Pagination,
} from "@/utils/api";
import { SortConfig } from "@/components/generic";
import { DEFAULT_PAGE_SIZE } from "@/config";

export interface UseContentBlocksOptions {
  /** Venue ID to filter content blocks by */
  venueId?: string | null | undefined;

  /** Content type to filter by */
  contentType?: string | undefined;

  /** Status to filter by */
  status?: number | undefined;

  /** Initial page size for pagination */
  pageSize?: number;

  /** Initial filters */
  initialFilters?: Partial<ContentBlockFilter>;

  /** Initial sort configuration */
  initialSort?: SortConfig[];

  /** Auto-load data on mount */
  autoLoad?: boolean;
}

export interface UseContentBlocksReturn {
  // Data state
  contentBlock: ContentBlock | null;
  contentBlocks: ContentBlock[];
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  isEmpty: boolean;

  // Search and filters
  filters: Partial<ContentBlockFilter>;
  sortBy: SortConfig[];

  // Pagination
  currentPage: number;
  pageSize: number;

  // Operations state
  isCreating: boolean;
  isUpdating: boolean;
  removingContentBlockId: string | null;
  selectedContentBlock: ContentBlock | null;

  // Core operations (matching API method names exactly)
  get: (id: string) => Promise<void>;
  list: (
    filter?: Partial<ContentBlockFilter>,
    pagination?: Pagination,
    sortBy?: SortConfig[],
    reset?: boolean
  ) => Promise<void>;
  getMany: (ids: string[]) => Promise<ContentBlock[]>;
  create: (params: ContentBlockInput) => Promise<ContentBlock | null>;
  update: (
    id: string,
    params: ContentBlockInput
  ) => Promise<ContentBlock | null>;
  remove: (id: string) => Promise<boolean>;

  // Additional convenience methods
  loadMore: () => Promise<void>;
  refresh: () => Promise<void>;

  // Filter operations
  setFilters: (filters: Partial<ContentBlockFilter>) => void;
  setSortBy: (sort: SortConfig[]) => void;

  // UI state management
  setSelectedContentBlock: (contentBlock: ContentBlock | null) => void;

  // Helper operations
  reset: () => void;

  // Computed values
  contentBlocksByVenue: Record<string, ContentBlock[]>;
  contentBlocksByType: Record<string, ContentBlock[]>;
  contentBlocksByStatus: Record<number, ContentBlock[]>;

  // Error handling
  lastError: string | null;
  clearError: () => void;
}

const DEFAULT_SORT: SortConfig[] = [{ key: "name", direction: "asc" }];

export const useContentBlocks = (
  options: UseContentBlocksOptions = {}
): UseContentBlocksReturn => {
  const {
    venueId,
    contentType,
    status,
    pageSize = DEFAULT_PAGE_SIZE,
    initialFilters = {},
    initialSort = DEFAULT_SORT,
    autoLoad = true,
  } = options;

  // Use refs for option values to avoid dependency issues
  const venueIdRef = useRef(venueId);
  const contentTypeRef = useRef(contentType);
  const statusRef = useRef(status);
  const pageSizeRef = useRef(pageSize);
  const sortByRef = useRef<SortConfig[]>(initialSort);

  // Update refs when options change
  useEffect(() => {
    venueIdRef.current = venueId;
  }, [venueId]);

  useEffect(() => {
    contentTypeRef.current = contentType;
  }, [contentType]);

  useEffect(() => {
    statusRef.current = status;
  }, [status]);

  useEffect(() => {
    pageSizeRef.current = pageSize;
  }, [pageSize]);

  // Data state
  const [contentBlock, setContentBlock] = useState<ContentBlock | null>(null);
  const [contentBlocks, setContentBlocks] = useState<ContentBlock[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);

  // Search and filters
  const [filters, setFilters] = useState<Partial<ContentBlockFilter>>(() => {
    const filter = { ...initialFilters };
    if (venueId) {
      filter.venueId = [venueId];
    }
    if (contentType) {
      filter.contentType = [contentType];
    }
    if (status !== undefined) {
      filter.status = [status];
    }
    return filter;
  });
  const [sortBy, setSortBy] = useState<SortConfig[]>(initialSort);

  // Update sortByRef when sortBy state changes
  useEffect(() => {
    sortByRef.current = sortBy;
  }, [sortBy]);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);

  // Operations state
  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [removingContentBlockId, setRemovingContentBlockId] = useState<
    string | null
  >(null);
  const [selectedContentBlock, setSelectedContentBlock] =
    useState<ContentBlock | null>(null);

  // Error handling
  const [lastError, setLastError] = useState<string | null>(null);

  // Computed values
  const isEmpty = useMemo(
    () => contentBlocks.length === 0 && !loading,
    [contentBlocks.length, loading]
  );

  const contentBlocksByVenue = useMemo(() => {
    return contentBlocks.reduce((acc, block) => {
      const venueId = block.venueId || "unknown";
      if (!acc[venueId]) {
        acc[venueId] = [];
      }
      acc[venueId].push(block);
      return acc;
    }, {} as Record<string, ContentBlock[]>);
  }, [contentBlocks]);

  const contentBlocksByType = useMemo(() => {
    return contentBlocks.reduce((acc, block) => {
      const type = block.contentType || "unknown";
      if (!acc[type]) {
        acc[type] = [];
      }
      acc[type].push(block);
      return acc;
    }, {} as Record<string, ContentBlock[]>);
  }, [contentBlocks]);

  const contentBlocksByStatus = useMemo(() => {
    return contentBlocks.reduce((acc, block) => {
      const status = block.status || 0;
      if (!acc[status]) {
        acc[status] = [];
      }
      acc[status].push(block);
      return acc;
    }, {} as Record<number, ContentBlock[]>);
  }, [contentBlocks]);

  // Helper function to update content block in list if it exists, or add it if not
  const updateOrAddContentBlockInList = useCallback(
    (updatedBlock: ContentBlock) => {
      setContentBlocks((prev) => {
        const existingIndex = prev.findIndex((cb) => cb.id === updatedBlock.id);
        if (existingIndex >= 0) {
          const newBlocks = [...prev];
          newBlocks[existingIndex] = updatedBlock;
          return newBlocks;
        } else {
          return [updatedBlock, ...prev];
        }
      });
    },
    []
  );

  // Helper function to remove content block from list if it exists
  const removeContentBlockFromList = useCallback((blockId: string) => {
    setContentBlocks((prev) => prev.filter((cb) => cb.id !== blockId));
  }, []);

  // Clear error function
  const clearError = useCallback(() => {
    setLastError(null);
  }, []);

  // Reset function
  const reset = useCallback(() => {
    setContentBlock(null);
    setContentBlocks([]);
    setLastError(null);
    setLoading(false);
    setLoadingMore(false);
    setHasMore(false);
    setCurrentPage(1);
    setIsCreating(false);
    setIsUpdating(false);
    setRemovingContentBlockId(null);
    setSelectedContentBlock(null);
  }, []);

  // Get single content block (matching API method name)
  const get = useCallback(
    async (id: string) => {
      setLoading(true);
      setLastError(null);

      try {
        const result = await ContentBlockService.get(id);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const blockData = result.data[0] as ContentBlock;
          setContentBlock(blockData);
          updateOrAddContentBlockInList(blockData);
          setLastError(null);
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to load content block";
          setLastError(errorMessage);
          setContentBlock(null);
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to load content block";
        setLastError(errorMessage);
        setContentBlock(null);
      } finally {
        setLoading(false);
      }
    },
    [updateOrAddContentBlockInList]
  );

  // List content blocks (matching API method name) - NO dependencies
  const list = useCallback(
    async (
      filter: Partial<ContentBlockFilter> = {},
      pagination?: Pagination,
      sortByParams?: SortConfig[],
      reset: boolean = false
    ) => {
      const targetPage = pagination?.page || 1;
      const targetPageSize = pagination?.pageSize || pageSizeRef.current;
      const isAppending = !reset;

      if (isAppending) {
        setLoadingMore(true);
      } else {
        setLoading(true);
        if (reset) {
          setContentBlocks([]);
          setCurrentPage(1);
        }
      }

      const combinedFilter = { ...filter };
      if (venueIdRef.current && !combinedFilter.venueId) {
        combinedFilter.venueId = [venueIdRef.current];
      }
      if (contentTypeRef.current && !combinedFilter.contentType) {
        combinedFilter.contentType = [contentTypeRef.current];
      }
      if (statusRef.current !== undefined && !combinedFilter.status) {
        combinedFilter.status = [statusRef.current];
      }

      setFilters(combinedFilter);
      if (sortByParams) setSortBy(sortByParams);

      const currentSortBy = sortByParams || sortByRef.current;

      try {
        const paginationParams: Pagination = {
          page: targetPage,
          pageSize: targetPageSize,
        };

        const sortParams = currentSortBy.map((sort) => ({
          name: sort.key,
          order: sort.direction.toUpperCase() as "ASC" | "DESC",
        }));

        const result = await ContentBlockService.list(
          combinedFilter as ContentBlockFilter,
          paginationParams,
          sortParams
        );

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const newBlocks = Array.isArray(result.data) ? result.data : [];

          if (isAppending) {
            setContentBlocks((prev) => {
              const existingIds = new Set(prev.map((cb) => cb.id));
              const uniqueNewBlocks = newBlocks.filter(
                (cb) => !existingIds.has(cb.id)
              );
              return [...prev, ...uniqueNewBlocks];
            });
          } else {
            setContentBlocks(newBlocks);
          }

          setHasMore(newBlocks.length === targetPageSize);
          setCurrentPage(targetPage);
          setLastError(null);
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to load content blocks";
          setLastError(errorMessage);
          if (!isAppending) setContentBlocks([]);
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to load content blocks";
        setLastError(errorMessage);
        if (!isAppending) setContentBlocks([]);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    []
  );

  // Get many content blocks by IDs (matching API method name)
  const getMany = useCallback(
    async (ids: string[]): Promise<ContentBlock[]> => {
      if (ids.length === 0) {
        return [];
      }

      setLoading(true);
      setLastError(null);

      try {
        const result = await ContentBlockService.getMany(ids);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const loadedBlocks = Array.isArray(result.data) ? result.data : [];
          loadedBlocks.forEach((block) => {
            updateOrAddContentBlockInList(block);
          });
          setLastError(null);
          return loadedBlocks;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to load content blocks";
          setLastError(errorMessage);
          return [];
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to load content blocks";
        setLastError(errorMessage);
        return [];
      } finally {
        setLoading(false);
      }
    },
    [updateOrAddContentBlockInList]
  );

  // Create content block (matching API method name)
  const create = useCallback(
    async (params: ContentBlockInput): Promise<ContentBlock | null> => {
      setIsCreating(true);
      setLastError(null);

      try {
        const blockToCreate = {
          ...params,
          venueId: params.venueId || venueIdRef.current || undefined,
          contentType: params.contentType || contentTypeRef.current,
          status:
            params.status !== undefined ? params.status : statusRef.current,
        };

        const result = await ContentBlockService.create(blockToCreate);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const newBlock = result.data[0] as ContentBlock;
          setContentBlock(newBlock);
          setContentBlocks((prev) => [newBlock, ...prev]);
          setLastError(null);
          return newBlock;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to create content block";
          setLastError(errorMessage);
          return null;
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to create content block";
        setLastError(errorMessage);
        return null;
      } finally {
        setIsCreating(false);
      }
    },
    []
  );

  // Update content block (matching API method name)
  const update = useCallback(
    async (
      id: string,
      params: ContentBlockInput
    ): Promise<ContentBlock | null> => {
      if (!id) {
        setLastError("Content block ID is required for updating");
        return null;
      }

      setIsUpdating(true);
      setLastError(null);

      try {
        const result = await ContentBlockService.update(id, params);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const updatedBlock = result.data[0] as ContentBlock;
          setContentBlock(updatedBlock);
          updateOrAddContentBlockInList(updatedBlock);
          setLastError(null);
          return updatedBlock;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to update content block";
          setLastError(errorMessage);
          return null;
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to update content block";
        setLastError(errorMessage);
        return null;
      } finally {
        setIsUpdating(false);
      }
    },
    [updateOrAddContentBlockInList]
  );

  // Remove content block (matching API method name)
  const remove = useCallback(
    async (id: string): Promise<boolean> => {
      if (!id) {
        setLastError("Content block ID is required for removal");
        return false;
      }

      setRemovingContentBlockId(id);
      setLastError(null);

      try {
        const result = await ContentBlockService.remove(id);

        if (result.code === OP_RESULT_CODES.OK) {
          setContentBlock(null);
          removeContentBlockFromList(id);
          setLastError(null);
          return true;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to remove content block";
          setLastError(errorMessage);
          return false;
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to remove content block";
        setLastError(errorMessage);
        return false;
      } finally {
        setRemovingContentBlockId(null);
      }
    },
    [removeContentBlockFromList]
  );

  // Load more content blocks
  const loadMore = useCallback(async () => {
    if (!loadingMore && hasMore) {
      const nextPage = currentPage + 1;
      await list(
        filters,
        { page: nextPage, pageSize: pageSizeRef.current },
        sortByRef.current,
        false
      );
    }
  }, [list, loadingMore, hasMore, currentPage, filters]);

  // Refresh content blocks
  const refresh = useCallback(async () => {
    await list(
      filters,
      { page: 1, pageSize: pageSizeRef.current },
      sortByRef.current,
      true
    );
  }, [list, filters]);

  // Auto-load effect
  const [hasAutoLoaded, setHasAutoLoaded] = useState(false);

  useEffect(() => {
    if (autoLoad && !hasAutoLoaded) {
      setHasAutoLoaded(true);
      const autoLoadFilters = { ...initialFilters };
      if (venueId) {
        autoLoadFilters.venueId = [venueId];
      }
      if (contentType) {
        autoLoadFilters.contentType = [contentType];
      }
      if (status !== undefined) {
        autoLoadFilters.status = [status];
      }

      list(autoLoadFilters, { page: 1, pageSize }, initialSort, true);
    }
  }, [autoLoad, hasAutoLoaded, list, pageSize, initialSort]);

  return {
    contentBlock,
    contentBlocks,
    loading,
    loadingMore,
    hasMore,
    isEmpty,
    filters,
    sortBy,
    currentPage,
    pageSize,
    isCreating,
    isUpdating,
    removingContentBlockId,
    selectedContentBlock,
    get,
    list,
    getMany,
    create,
    update,
    remove,
    loadMore,
    refresh,
    setFilters,
    setSortBy,
    setSelectedContentBlock,
    reset,
    contentBlocksByVenue,
    contentBlocksByType,
    contentBlocksByStatus,
    lastError,
    clearError,
  };
};
